import { Step } from '@cabal/shared/domain/session/enums.js';
import { TeamRole } from '@cabal/shared/domain/session/team/enums.js';
import { MemberHealthStatus } from '@cabal/shared/domain/session/team/member/enums.js';
import { type Member } from '@cabal/shared/domain/session/team/member/types.js';
import { ColorType } from '@/theme/colors';

export interface ShouldMemberBlinkInput {
  haltedOnStep: Step | null | undefined;
  role: TeamRole;
  /** Whether this member owns a round-focus product this round. Gates the
   *  Reflect/Write pulse so only assigned operatives render as active. */
  isRoundFocusWriter: boolean;
}

export interface ResolveMemberDotStateInput {
  healthStatus: MemberHealthStatus;
  shouldBlink: boolean;
}

export interface MemberDotState {
  visible: boolean;
  color: ColorType;
  blink: boolean;
}

const ROLE_PRIORITY: Record<TeamRole, number> = {
  [TeamRole.Chair]: 0,
  [TeamRole.Watchdog]: 1,
  [TeamRole.Operative]: 2,
  [TeamRole.Envoy]: 2,
};

// Mirrors the BL's step-config:
// apps/bl/src/business-logic/orchestration/context/config/step-config.ts
const BLINKING_STEPS_BY_ROLE: Record<TeamRole, ReadonlySet<Step>> = {
  [TeamRole.Chair]: new Set([Step.Bootstrap, Step.PlanAssign]),
  [TeamRole.Watchdog]: new Set([Step.Inspection]),
  [TeamRole.Operative]: new Set([
    Step.Reflect,
    Step.Write,
    Step.PeerReview,
  ]),
  // Presentation is the Envoy's step; Envoys are filtered from the FE
  // member list so it never produces a visible blink.
  [TeamRole.Envoy]: new Set(),
};

// Operative steps where only the members actually assigned a round-focus
// product are working; non-owners stay idle. PeerReview is intentionally
// excluded — every operative reviews, so it stays a blanket pulse.
const ROUND_FOCUS_GATED_STEPS: ReadonlySet<Step> = new Set([
  Step.Reflect,
  Step.Write,
]);

/**
 * Resolve the role text actually shown on a member chip. Operatives with
 * a non-empty `teamRole` show that instead of the bare `Operative` label.
 * Returned as a stable string so callers can use it for equality checks
 * (e.g. live-flash diffs) and for rendering.
 */
export function deriveMemberDisplayRole(member: Member): string {
  if (member.role === TeamRole.Operative && member.teamRole) {
    return member.teamRole;
  }
  return member.role;
}

export function compareSessionMembers(a: Member, b: Member): number {
  const roleDelta = ROLE_PRIORITY[a.role] - ROLE_PRIORITY[b.role];
  if (roleDelta !== 0) {
    return roleDelta;
  }

  if (a.displayOrder !== b.displayOrder) {
    return a.displayOrder - b.displayOrder;
  }

  const nameDelta = a.name.localeCompare(b.name);
  if (nameDelta !== 0) {
    return nameDelta;
  }

  return a.id.localeCompare(b.id);
}

export function shouldMemberBlink(input: ShouldMemberBlinkInput): boolean {
  if (!input.haltedOnStep) {
    return false;
  }

  if (!BLINKING_STEPS_BY_ROLE[input.role].has(input.haltedOnStep)) {
    return false;
  }

  // In Reflect/Write only the operatives assigned a round-focus item are
  // actually working; non-owners stay idle even though the step is "theirs".
  if (
    input.role === TeamRole.Operative &&
    ROUND_FOCUS_GATED_STEPS.has(input.haltedOnStep) &&
    !input.isRoundFocusWriter
  ) {
    return false;
  }

  return true;
}

export function resolveMemberDotState(input: ResolveMemberDotStateInput): MemberDotState {
  if (input.healthStatus === MemberHealthStatus.Errored) {
    return {
      visible: true,
      color: ColorType.Danger,
      blink: input.shouldBlink,
    };
  }

  if (input.healthStatus === MemberHealthStatus.Laggy) {
    return {
      visible: true,
      color: ColorType.Warn,
      blink: input.shouldBlink,
    };
  }

  if (!input.shouldBlink) {
    return {
      visible: false,
      color: ColorType.White,
      blink: false,
    };
  }

  return {
    visible: true,
    color: ColorType.White,
    blink: true,
  };
}
